import { Link, useParams } from "react-router-dom";
import { Card, CardBody, CardHeader, EmptyState, ErrorState, LoadingState, TextArea, Button, formatDate } from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, meetingApi } from "../../lib/api";
import { useState, useEffect } from "react";
import { CandidateReview } from "./CandidateReview";
import { DemoNotice, meetingLabel, stateTone } from "./MeetingShared";
import { Badge } from "@odingard/design-system";
import { MeetingActionPanel, MeetingLifecycleActions } from "./MeetingActions";

function SourceRow({
  orgId,
  meetingId,
  source,
  personName,
  onChanged,
}: {
  orgId: string;
  meetingId: string;
  personName: (id: string) => string;
  source: {
    id: string;
    filename: string;
    documentVersion: number;
    contentHash: string;
    detachedAt: string | null;
    detachedBy: string | null;
    detachmentReason: string | null;
    attachedBy: string;
    attachedAt: string;
  };
  onChanged: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function detach() {
    setBusy(true);
    setError(null);
    try {
      await meetingApi.detachSource(orgId, meetingId, source.id, reason.trim());
      setReason("");
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The source could not be detached.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div>
      <p>
        <strong>{source.filename}</strong> · Documents version {source.documentVersion}{" "}
        <small title={source.contentHash}>provenance available</small>
      </p>
      <p>Attached by {personName(source.attachedBy)} on {formatDate(source.attachedAt)}</p>
      {source.detachedAt ? (
        <p>
          Detached by {personName(source.detachedBy ?? "")} on {formatDate(source.detachedAt)}
          {source.detachmentReason ? `: ${source.detachmentReason}` : ""}
        </p>
      ) : (
        <>
          <TextArea
            aria-label={`Reason for detaching ${source.filename}`}
            placeholder="Reason for detaching source"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Button size="sm" disabled={busy || !reason.trim()} onClick={() => void detach()}>
            Detach source
          </Button>
          {error && <p role="alert">{error}</p>}
        </>
      )}
    </div>
  );
}

export function MeetingDetail() {
  const { activeOrg } = useAuth();
  const { meetingId = "" } = useParams();
  const orgId = activeOrg?.id ?? "";
  const request = useAsync(async () => {
    const [meeting, candidates, decisions, commitments, sources, timeline, extractions] = await Promise.all([
      meetingApi.meeting(orgId, meetingId),
      meetingApi.candidates(orgId, meetingId),
      meetingApi.decisions(orgId, meetingId),
      meetingApi.commitments(orgId, meetingId),
      meetingApi.sources(orgId, meetingId),
      meetingApi.timeline(orgId, meetingId),
      meetingApi.extractions(orgId, meetingId),
    ]);
    return { meeting, candidates, decisions, commitments, sources, timeline, extractions };
  }, [orgId, meetingId]);
  const [members, setMembers] = useState<{ id: string; displayName: string; email: string }[]>([]);
  useEffect(() => { if (orgId) void api.members(orgId).then((response) => setMembers(response.members)).catch(() => undefined); }, [orgId]);

  if (request.loading && !request.data) return <LoadingState label="Loading meeting…" />;
  if (request.error) return <ErrorState description={request.error.message} onRetry={request.reload} />;
  const payload = request.data;
  const meeting = payload?.meeting.data.meeting;
  if (!meeting) return <EmptyState title="Meeting not found" description="The meeting may be removed or outside your organization." />;

  const candidates = payload.candidates.data.candidates;
  const extractions = payload.extractions.data.extractions;
  const latestExtraction = extractions[extractions.length - 1];
  const personName = (id: string) => members.find((member) => member.id === id)?.displayName ?? (id ? "Former member" : "Unknown member");
  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/studio/meeting-ai">Meeting AI</Link>
          <h1>{meeting.title}</h1>
        </div>
        <Badge tone={stateTone(meeting.state)}>{meetingLabel(meeting.state)}</Badge>
      </div>
      <DemoNotice demoMode={payload.meeting.demoMode} />
      <Card><CardHeader title="Identity" /><CardBody><p>{meeting.title}</p><p>Recorded {formatDate(meeting.createdAt)}</p></CardBody></Card>
      <Card><CardHeader title="Status" /><CardBody><Badge tone={stateTone(meeting.state)}>{meetingLabel(meeting.state)}</Badge></CardBody></Card>
      <Card><CardHeader title="Owner" /><CardBody><p>{personName(meeting.createdBy)}</p></CardBody></Card>
      <Card>
        <CardHeader title="Participants" />
        <CardBody>
          {(meeting.participants ?? []).length === 0 ? (
            <EmptyState title="No participants recorded" description="Add internal Studio members or external attendees to this governed meeting." />
          ) : (
            (meeting.participants ?? []).map((participant) => {
              const name = participant.participantKind === "internal"
                ? personName(participant.userId ?? "")
                : participant.externalName ?? "External attendee";
              return (
                <p key={participant.id}>
                  <strong>{name}</strong> · {participant.participantKind} · {meetingLabel(participant.role)}
                  {participant.participantKind === "external" && participant.externalEmail ? ` · ${participant.externalEmail}` : ""}
                  {` · Added by ${personName(participant.addedBy)} on ${formatDate(participant.addedAt)}`}
                  {participant.removedAt
                    ? ` · Removed by ${personName(participant.removedBy ?? "")} on ${formatDate(participant.removedAt)}`
                    : ""}
                </p>
              );
            })
          )}
        </CardBody>
      </Card>
      <Card><CardHeader title="Risk and condition" /><CardBody><p>Classification: {meetingLabel(meeting.confidentialityClassification)}</p><p>State: {meetingLabel(meeting.state)}</p><p>Pending proposals in this meeting: {candidates.filter((candidate) => candidate.disposition === "PENDING").length}</p></CardBody></Card>
      <Card>
        <CardHeader title="Relationships" />
        <CardBody>
          <p><strong>Asserted decisions:</strong> {payload.decisions.data.decisions.length}</p>
          <p><strong>Asserted commitments:</strong> {payload.commitments.data.commitments.length}</p>
          <p><strong>Derived proposals:</strong> {candidates.filter((candidate) => candidate.disposition === "PENDING").length}</p>
          <p>Derived proposals remain separate from asserted facts; each retains its basis and confidence below.</p>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Timeline" />
        <CardBody>
          <p role="status">
            Audit chain integrity: {payload.timeline.data.integrity.intact
              ? "verified"
              : `broken at sequence ${payload.timeline.data.integrity.firstBrokenPosition ?? "unknown"}`}
          </p>
          {payload.timeline.data.entries.map((entry) => (
            <p key={entry.id}>
              <strong>{entry.action}</strong> · <span>{personName(entry.actorId)}</span> · {formatDate(entry.occurredAt)}
              {" · Sequence "}{entry.sequenceNo}
              {" · Event hash "}
              <span title={entry.eventHash} aria-label={`Event hash ${entry.eventHash}`}>{entry.eventHash.slice(0, 12)}…</span>
              {entry.previousHash ? (
                <>
                  {" · Previous hash "}
                  <span title={entry.previousHash} aria-label={`Previous hash ${entry.previousHash}`}>{entry.previousHash.slice(0, 12)}…</span>
                </>
              ) : null}
              {entry.refusalCode ? ` · Refused: ${entry.refusalCode}` : ""}
            </p>
          ))}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Evidence" />
        <CardBody>
          {payload.sources.data.sources.length === 0 && <EmptyState title="No evidence attached" description="Attach a caller-authorized Documents reference to ground this record." />}
          {payload.sources.data.sources.map((source) => (
            <SourceRow
              key={source.id}
              orgId={orgId}
              meetingId={meetingId}
              source={source}
              personName={personName}
              onChanged={request.reload}
            />
          ))}
        </CardBody>
      </Card>
      <Card><CardHeader title="Activity" /><CardBody>{extractions.map((extraction) => <p key={extraction.id}>{extraction.provider} / {extraction.model}: {meetingLabel(extraction.outcome)} ({extraction.candidateCount} candidates)</p>)}</CardBody></Card>
      <Card><CardHeader title="Approvals" /><CardBody>{payload.decisions.data.decisions.map((decision) => <p key={decision.id}>Decision confirmed by {personName(decision.confirmingActor)} at {formatDate(decision.confirmedAt)}: {decision.statement}</p>)}<p>Meeting state: {meetingLabel(meeting.state)}</p></CardBody></Card>
      <Card><CardHeader title="Tasks and deadlines" /><CardBody>{payload.commitments.data.commitments.map((commitment) => <p key={commitment.id}>{commitment.statement} — {personName(commitment.ownerUserId)}, due {commitment.dueDate ?? "no due date"} ({meetingLabel(commitment.derivedStatus)})</p>)}</CardBody></Card>
      <Card><CardHeader title="Intelligence" /><CardBody><p>Derived content is labelled and requires verification.</p></CardBody></Card>
      <CandidateReview orgId={orgId} meetingId={meetingId} candidates={candidates} extractionOutcome={latestExtraction?.outcome} onChanged={request.reload} />
      <MeetingActionPanel orgId={orgId} meetingId={meetingId} members={members} onChanged={request.reload} />
      <MeetingLifecycleActions orgId={orgId} meeting={meeting} onChanged={request.reload} />
      <Card><CardHeader title="Audit and history" /><CardBody><p>Append-only governed activity is shown in the timeline above.</p></CardBody></Card>
    </>
  );
}
